export const injectHrefLang = (hreflang: string, href: string, id: string) => {
    if (typeof window === "undefined") {
        return;
    }

    let existingLink = document.getElementById(id) as HTMLLinkElement | null;

    if (existingLink) {
        // Update if the element already exists
        existingLink.setAttribute("hreflang", hreflang);
        existingLink.setAttribute("href", href);
    } else {
        // Create and append new <link> tag
        const linkTag = document.createElement("link");
        linkTag.setAttribute("key", hreflang);
        linkTag.setAttribute("rel", "alternate");
        linkTag.setAttribute("hreflang", hreflang);
        linkTag.setAttribute("href", href);
        linkTag.setAttribute("id", id);
        document.head.appendChild(linkTag);
    }
};

export const removeStaleHrefLangs = (validIds: string[]) => {
    if (typeof window === "undefined") {
        return;
    }

    const linkTags = document.querySelectorAll('link[rel="alternate"][hreflang]');

    linkTags.forEach(link => {
        const id = link.getAttribute("id");
        if (id && !validIds.includes(id)) {
            link.remove();
        }
    });
};

export const removeMetadata = (id: string) => {
    if (typeof window === "undefined") {
        return;
    }

    const checkAndRemoveScripts = () => {
        const scriptList = document.querySelectorAll("script[type='application/ld+json']");

        if (scriptList.length === 0) {
            setTimeout(checkAndRemoveScripts, 100);
            return;
        }

        scriptList.forEach(script => {
            const scriptId = script.getAttribute("id");

            if (scriptId === id) {
                script.parentNode?.removeChild(script);
            }
        });
    };

    if (document.readyState === "complete") {
        checkAndRemoveScripts();
    } else {
        window.addEventListener("load", checkAndRemoveScripts);
    }
};

export const injectMetadata = (metadata: string, id: string) => {
    if (typeof window === "undefined") {
        return;
    }

    let jsonLdScript = document.querySelector('script[type="application/ld+json"]:not([id])');
    if (!jsonLdScript) {
        jsonLdScript = document.createElement("script");
        jsonLdScript.setAttribute("type", "application/ld+json");
        document.head.appendChild(jsonLdScript);
    }

    let scriptTag = document.getElementById(id);
    if (scriptTag) {
        scriptTag.innerHTML = metadata;
    } else {
        scriptTag = document.createElement("script");
        scriptTag.setAttribute("type", "application/ld+json");
        scriptTag.id = id;
        scriptTag.innerHTML = metadata;
        document.head.appendChild(scriptTag);
    }
};

export const setProductMetadata = (props: any) => {
    const {
        productState: { value: product },
        productInfo,
        catalogPageState,
        logo,
        currentUrl,
    } = props;

    const productData = {
        "@context": "https://schema.org/",
        "@type": "Product",
        name: product?.productNumber,
        image: product?.largeImagePath,
        description: product?.productTitle,
        brand: {
            "@type": "Brand",
            name: product?.brand,
        },
        offers: {
            "@type": "Offer",
            url: catalogPageState.value.uri,
            priceCurrency: "USD",
            price: productInfo != null ? (productInfo.pricing?.actualPrice ?? 0) : 0,
        },
    } as any;

    const breadCrums = catalogPageState.value.breadCrumbs;
    const breadCrumbListData = {
        "@context": "https://schema.org/",
        "@type": "BreadcrumbList",
        itemListElement: breadCrums?.map((subCategory: any, index: number) => ({
            "@type": "ListItem",
            position: index + 1,
            name: subCategory?.text,
            item: `${currentUrl}${subCategory?.url != null ? subCategory?.url : ""}`,
        })),
    } as any;

    const organizationData = {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Iewc",
        alternateName: "",
        url: `${currentUrl}`,
        logo: `${logo}`,
        sameAs: [""],
    } as any;

    setTimeout(() => {
        injectMetadata(JSON.stringify(productData), "productDetail_ProductSchema");
        injectMetadata(JSON.stringify(breadCrumbListData), "productDetail_BreadCrumbListSchema");
        injectMetadata(JSON.stringify(organizationData), "productDetail_OrganizationSchema");
    }, 500);

    setWebsiteMetadata({ currentUrl });
};

export const setProductListMetadata = (props: any) => {
    const { products, catalogPageState, currentUrl } = props;
    let logo = "";
    if (typeof document !== "undefined") {
        logo = document.querySelector(".home-page-link img")?.getAttribute("src") || "";
    }

    const itemListData = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: products?.map((product: any, index: number) => ({
            "@type": "ListItem",
            position: index + 1,
            name: product?.productNumber,
            url: `${product?.uri}`,
        })),
    } as any;

    const productListData = {
        "@context": "https://schema.org/",
        "@type": "Product",
        products: products?.map((product: any) => ({
            name: product?.productNumber,
            image: product?.largeImagePath,
            description: product?.productTitle,
            brand: {
                "@type": "Brand",
                name: product?.brand ?? "",
            },
            offers: {
                "@type": "Offer",
                url: catalogPageState.value.uri,
                priceCurrency: "USD",
                price: "",
            },
        })),
    } as any;

    const breadCrums = catalogPageState.value.breadCrumbs;
    const breadCrumbListData = {
        "@context": "https://schema.org/",
        "@type": "BreadcrumbList",
        itemListElement: breadCrums?.map((subCategory: any, index: number) => ({
            "@type": "ListItem",
            position: index + 1,
            name: subCategory?.text,
            item: `${currentUrl}${subCategory?.url != null ? subCategory?.url : ""}`,
        })),
    } as any;

    const organizationData = {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Iewc",
        alternateName: "",
        url: `${currentUrl}`,
        logo: `${logo}`,
        sameAs: [""],
    } as any;

    injectMetadata(JSON.stringify(itemListData), "productList_itemListSchema");
    injectMetadata(JSON.stringify(productListData), "productList_ProductSchema");
    injectMetadata(JSON.stringify(breadCrumbListData), "productList_BreadCrumbListSchema");
    injectMetadata(JSON.stringify(organizationData), "productList_OrganizationSchema");

    setWebsiteMetadata({ currentUrl });
};

export const setBrandListMetadata = (props: any) => {
    const { allBrandsDataView, currentUrl, currentPage, location, catalogPageState } = props;

    let logo = "";
    if (typeof document !== "undefined") {
        logo = document.querySelector(".home-page-link img")?.getAttribute("src") || "";
    }

    const itemBrandListData = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "List of Brands",
        description: "",
        url: `${currentUrl}${location.pathname}`,
        numberOfItems: allBrandsDataView.value.length,
        itemListElement: allBrandsDataView.value.map((brand: any, index: number) => ({
            "@type": "ListItem",
            position: index + 1,
            url: `${brand.detailPagePath}`,
            name: brand.name,
            item: {
                "@type": "Brand",
                name: brand.name,
                logo: brand.logoLargeImagePath ? brand.logoLargeImagePath : brand.logoSmallImagePath,
                description: brand.metaDescription,
            },
        })),
    } as any;

    const organizationData = {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Iewc",
        alternateName: "",
        url: `${currentUrl}`,
        logo: `${logo}`,
        sameAs: [""],
    } as any;

    const breadCrumbs = catalogPageState.id;
    const breadCrumbListData = {
        "@context": "https://schema.org/",
        "@type": "BreadcrumbList",
        itemListElement: [
            {
                "@type": "ListItem",
                position: 1,
                name: "Brands",
                item: `${currentUrl}${breadCrumbs != null ? breadCrumbs : ""}`,
            },
        ],
    } as any;

    injectMetadata(JSON.stringify(organizationData), "brandList_OrganizationSchema");
    injectMetadata(JSON.stringify(breadCrumbListData), "brandList_BreadCrumbListSchema");
    injectMetadata(JSON.stringify(itemBrandListData), "brandList_ItemListSchema");
    setWebsiteMetadata({ currentUrl });
};

export const setCategoryMetadata = (props: any) => {
    const { subCategories, currentUrl } = props;
    let logo = "";
    if (typeof document !== "undefined") {
        logo = document.querySelector(".home-page-link img")?.getAttribute("src") || "";
    }

    const itemListData = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: subCategories?.map((subCategory: any, index: number) => ({
            "@type": "ListItem",
            position: index + 1,
            name: subCategory?.name,
            url: `${currentUrl}${subCategory?.path}`,
        })),
    } as any;

    const breadCrumbListData = {
        "@context": "https://schema.org/",
        "@type": "BreadcrumbList",
        itemListElement: subCategories?.map((subCategory: any, index: number) => ({
            "@type": "ListItem",
            position: index + 1,
            name: subCategory?.name,
            item: `${currentUrl}${subCategory?.path}`,
        })),
    } as any;

    const organizationData = {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Iewc",
        alternateName: "",
        url: `${currentUrl}`,
        logo: `${logo}`,
        sameAs: [""],
    } as any;

    injectMetadata(JSON.stringify(itemListData), "categoryListItemList");
    injectMetadata(JSON.stringify(breadCrumbListData), "categoryListBreadCrumbList");
    injectMetadata(JSON.stringify(organizationData), "categoryDetailOrganization");
    setWebsiteMetadata({ currentUrl });
};

export const setHomePageMetadata = (props: any) => {
    const { currentUrl } = props;

    let logo = "";
    if (typeof document !== "undefined") {
        logo = document.querySelector(".home-page-link img")?.getAttribute("src") || "";
    }

    const organizationData = {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Iewc",
        alternateName: "",
        url: `${currentUrl}`,
        logo: `${logo}`,
        sameAs: [""],
    } as any;

    let anchors: any[] = [];
    if (typeof document !== "undefined") {
        anchors = Array.from(document.querySelectorAll("a.verndaleGA4__popular-category"));
    }

    if (anchors && anchors.length > 0) {
        const itemListData = {
            "@context": "https://schema.org",
            "@type": "ItemList",
            itemListElement: Array.from(anchors)
                .filter((anchor: any) => {
                    const href = anchor.getAttribute("href");
                    return href && !href.includes("/services");
                })
                .map((anchor: any, index: number) => ({
                    "@type": "ListItem",
                    position: index + 1,
                    name: anchor.textContent?.trim(),
                    url: anchor.getAttribute("href"),
                })),
        } as any;

        injectMetadata(JSON.stringify(itemListData), "homePage_ItemListSchema");
    }

    injectMetadata(JSON.stringify(organizationData), "homePage_OrganizationSchema");
    setWebsiteMetadata({ currentUrl });
};

export const setContactUsPageMetadata = (props: any) => {
    const { currentUrl, currentPage } = props;

    const organizationData = {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Iewc",
        alternateName: "",
    } as any;

    const breadCrumbs = currentPage.fields.urlSegment;
    const breadCrumbListData = {
        "@context": "https://schema.org/",
        "@type": "BreadcrumbList",
        itemListElement: [
            {
                "@type": "ListItem",
                position: 1,
                name: "Contact Us",
                item: `${currentUrl}`,
            },
        ],
    } as any;

    injectMetadata(JSON.stringify(organizationData), "contactUsPage_OrganizationSchema");
    injectMetadata(JSON.stringify(breadCrumbListData), "contactUsPage_BreadCrumbListSchema");
    setWebsiteMetadata({ currentUrl });
};

export const setRequestQuotePageMetadata = (props: any) => {
    const { currentUrl, currentPage } = props;

    const organizationData = {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Iewc",
        alternateName: "",
    } as any;

    const breadCrumbs = currentPage.fields.urlSegment;
    const breadCrumbListData = {
        "@context": "https://schema.org/",
        "@type": "BreadcrumbList",
        itemListElement: [
            {
                "@type": "ListItem",
                position: 1,
                name: "Request a Quote",
                item: `${currentUrl}`,
            },
        ],
    } as any;

    injectMetadata(JSON.stringify(organizationData), "requestQuotePage_OrganizationSchema");
    injectMetadata(JSON.stringify(breadCrumbListData), "requestQuotePage_BreadCrumbListSchema");
    setWebsiteMetadata({ currentUrl });
};

export const setLocationFinderPageMetadata = (props: any) => {
    const { locations, currentUrl, currentPage } = props;

    const organizationData = {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Iewc",
        alternateName: "",
    } as any;

    const breadCrumbListData = {
        "@context": "https://schema.org/",
        "@type": "BreadcrumbList",
        itemListElement: locations?.map((location: any, index: number) => ({
            "@type": "ListItem",
            position: index + 1,
            name: location.name,
            item: location.uri,
        })),
    } as any;

    const itemListData = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: locations?.map((location: any, index: number) => ({
            "@type": "ListItem",
            position: index + 1,
            name: location.name,
            url: location.uri,
        })),
    } as any;

    const locationBusinessData = {
        "@context": "https://schema.org/",
        "@type": "ItemList",
        itemListElement: locations?.map((location: any, index: number) => ({
            "@type": "LocalBusiness",
            name: location.name,
            url: location.uri,
            address: {
                "@type": "PostalAddress",
                streetAddress: `${location.address1} ${location.address2}`,
                addressLocality: location.city,
                addressRegion: location.state,
                postalCode: location.postalCode,
            },
            telephone: location.phone,
        })),
    };

    injectMetadata(JSON.stringify(organizationData), "locationFinderPage_OrganizationSchema");
    injectMetadata(JSON.stringify(breadCrumbListData), "locationFinderPage_BreadCrumbListSchema");
    injectMetadata(JSON.stringify(itemListData), "locationFinderPage_ItemListSchema");
    injectMetadata(JSON.stringify(locationBusinessData), "locationFinderPage_LocationBusinessSchema");
    setWebsiteMetadata({ currentUrl });
};

export const setStandardPageMetadata = (props: any) => {
    const { locations, currentUrl, currentPage } = props;
    let logo = "";
    if (typeof document !== "undefined") {
        logo = document.body.querySelector(".home-page-link img")?.getAttribute("src") || "";
    }

    const organizationData = {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Iewc",
        alternateName: "",
    } as any;

    const breadCrumbs = currentPage.fields.urlSegment;
    const breadCrumbListData = {
        "@context": "https://schema.org/",
        "@type": "BreadcrumbList",
        itemListElement: [
            {
                "@type": "ListItem",
                position: 1,
                name: "Standard Page",
                item: `${currentUrl}`,
            },
        ],
    } as any;

    if (currentPage.type === "BlogDetailPage") {
        const articleData = {
            "@context": "https://schema.org",
            "@type": "Article",
            mainEntityOfPage: {
                "@type": "WebPage",
                "@id": `${currentUrl}`,
            },
            headline: currentPage.fields.title,
            description: currentPage.fields.summary,
            image: currentPage.fields.thumbnail,
            author: {
                "@type": "Person",
                name: currentPage.fields.author,
                url: currentPage.fields.authorUrl.value,
            },
            publisher: {
                "@type": "Organization",
                name: "Iewc",
                logo: {
                    "@type": "ImageObject",
                    url: logo,
                },
            },
            datePublished: currentPage.fields.publishDate,
        } as any;

        injectMetadata(JSON.stringify(articleData), "standardPage_ArticleSchema");
    }

    injectMetadata(JSON.stringify(organizationData), "standardPage_OrganizationSchema");
    injectMetadata(JSON.stringify(breadCrumbListData), "standardPage_BreadCrumbListSchema");
    setWebsiteMetadata({ currentUrl });
};

export const setWebsiteMetadata = (props: any) => {
    const { currentUrl, searchTerm, searchUrl } = props;
    let logo = "";
    if (typeof document !== "undefined") {
        logo = document.querySelector(".home-page-link img")?.getAttribute("src") || "";
    }
    const websiteData = {
        "@context": "https://schema.org/",
        "@type": "WebSite",
        name: "Iewc",
        url: `${currentUrl}`,
        potentialAction: {
            "@type": "SearchAction",
            target: searchTerm ? `${searchUrl}` : "",
            "query-input": searchTerm ? `required name=${searchTerm}` : "",
        },
    } as any;

    injectMetadata(JSON.stringify(websiteData), "websiteDataSchema");
};

export const setHrefLangMetadata = (props: any) => {
    removeAllHrefLangs();
    const { hreflangList, currentUrl, currentPage, location, languages } = props;

    if (currentPage.type === "Page") {
        setHrefLangMetadataForStantardPages(props);
        return;
    }

    const validIds: string[] = [];
    const PageName = currentPage.name
        .trim()
        .replace(/\s+/g, "")
        .replace(/[^a-zA-Z0-9-_]/g, "");

    const currentSiteLangCodes = getAllLangCodes(languages ?? []);
    const langCodesForPathStrip = [
        ...currentSiteLangCodes,
        ...hreflangList.map((h: string) => h.split("|")[0]).filter(Boolean),
    ];

    hreflangList.forEach((hreflang: any) => {
        const [languageCode, hreflangUrl] = hreflang.split("|");

        const hostname = new URL(hreflangUrl).hostname;
        const domainParts = hostname.replace(/^www\./, "").split(".");

        let middlePart = domainParts[0];
        if (domainParts.length > 1) {
            middlePart += `-${domainParts[1]}`;
        }

        const cleanId = `${languageCode}_${PageName}_${middlePart}_IEWC-Site`;

        const pathForThisLang = stripLanguagePrefix(location.pathname, langCodesForPathStrip);

        const baseHref = updatedWebsiteUrlWithOutLanguage(hreflangUrl);

        const fullHref = normalizeUrl(baseHref, pathForThisLang);
        const fullHrefWithLanguage = updateWebsiteUrlWithLanguage(fullHref, languageCode, currentSiteLangCodes);

        injectHrefLang(languageCode, fullHrefWithLanguage, cleanId);

        validIds.push(cleanId);
    });

    removeStaleHrefLangs(validIds);
};

function cleanPathname(hreflangUrl: string, pathname: string, allLanguageCodes: string[]): string {
    const url = new URL(hreflangUrl);
    const hreflangPath = url.pathname.replace(/\/+$/, ""); // remove trailing slashes

    const pathSegments = pathname.split("/").filter(Boolean);

    if (pathSegments.length === 0) {
        return ""; // No extra path to append
    }

    const prefixes = allLanguageCodes.map(code => code.split("-")[0]); // ["en", "es", "de", "fr"]
    const pathPrefix = pathSegments[0];

    const hreflangSegments = hreflangPath.split("/").filter(Boolean);

    const hreflangIncludesPrefix = hreflangSegments.includes(pathPrefix);

    if (hreflangIncludesPrefix) {
        return ""; // Do not append anything; prefix already exists
    }

    return `/${pathSegments.join("/")}`; // Safe to append
}

function removeAllHrefLangs() {
    if (typeof document !== "undefined") {
        const hreflangLinks = document.querySelectorAll('link[rel="alternate"][hreflang]');
        hreflangLinks.forEach(link => link.remove());
    }
}

function getAllLangCodes(langs: any[] = []) {
    return langs.map((l: any) => l.languageCode);
}

export const setHrefLangMetadataForStantardPages = (props: any) => {
    const { hreflangList, currentUrl, currentPage, location, languages } = props;

    const validIds: string[] = [];

    const PageName = currentPage.name
        .trim()
        .replace(/\s+/g, "")
        .replace(/[^a-zA-Z0-9-_]/g, "");

    const availableWebsites = currentPage.generalFields?.availableWebsites;
    const xDefaultSite = currentPage.generalFields?.xDefaultSite?.value;

    const currentSiteLangCodes = getAllLangCodes(languages ?? []);
    const langCodesFromLinkedSites = (availableWebsites ?? []).flatMap((website: any) =>
        (website.fields?.languages ?? []).map((item: any) => item.fields.language.languageCode),
    );
    const langCodesForPathStrip = [...currentSiteLangCodes, ...langCodesFromLinkedSites];

    removeAllHrefLangs();

    // x-default: always remove the language segment from the path
    if (xDefaultSite) {
        const hostname = new URL(xDefaultSite).hostname;
        const domainParts = hostname.replace(/^www\./, "").split(".");
        const middlePart = domainParts[0];
        const cleanId = `x-default_${PageName}_${middlePart}`;

        const baseHref = updatedWebsiteUrlWithOutLanguage(xDefaultSite);
        const pathForXDefault = stripLanguagePrefix(location.pathname, langCodesForPathStrip);
        const xDefaultHref = normalizeUrl(baseHref, pathForXDefault);

        injectHrefLang("x-default", xDefaultHref, cleanId);
        validIds.push(cleanId);
    }

    if (availableWebsites && availableWebsites.length > 0) {
        availableWebsites.forEach((website: any) => {
            const websiteLanguages = website.fields.languages;
            const websiteTitle = website.fields.websiteTitle;
            const websiteUrl = website.fields.websiteUrl.value;

            const hostname = new URL(websiteUrl).hostname;
            const domainParts = hostname.replace(/^www\./, "").split(".");
            const middlePart = domainParts[0];

            websiteLanguages.forEach((item: any) => {
                const languageCode = item.fields.language.languageCode;
                const cleanId = `${languageCode}_${PageName}_${middlePart}_${websiteTitle}`;

                const pathForThisLang = stripLanguagePrefix(location.pathname, langCodesForPathStrip);

                const baseHref = updatedWebsiteUrlWithOutLanguage(websiteUrl);

                const fullHref = normalizeUrl(baseHref, pathForThisLang);
                const fullHrefWithLanguage = updateWebsiteUrlWithLanguage(fullHref, languageCode, currentSiteLangCodes);

                injectHrefLang(languageCode, fullHrefWithLanguage, cleanId);
                validIds.push(cleanId);
            });
        });
    }

    removeStaleHrefLangs(validIds);
};

function stripLanguagePrefix(pathname: string, allLanguageCodes: string[]) {
    const allPrefixes = allLanguageCodes.map(code => code.split("-")[0].toLowerCase());
    const segments = pathname.split("/").filter(Boolean);

    if (segments.length > 0 && allPrefixes.includes(segments[0].toLowerCase())) {
        segments.shift();
    }

    return `/${segments.join("/")}`;
}

function normalizeUrl(baseUrl: string, path: string): string {
    return `${baseUrl.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
}

// this function will work if is needed to remove the language prefix from the website url for  /about-us
function updatedWebsiteUrlWithOutLanguage(websiteUrl: string): string {
    const url = new URL(websiteUrl);
    url.pathname = "/";
    return url.toString();
}

// THis function will work if is needed to add a language prefix before the current path for ex: /en/about-us
function updateWebsiteUrlWithLanguage(websiteUrl: string, languageCode: string, allLanguageCodes: string[]): string {
    const langPrefix = languageCode.split("-")[0];
    const url = new URL(websiteUrl);
    const pathSegments = url.pathname.split("/").filter(Boolean);

    if (langPrefix !== "en" && langPrefix !== "x") {
        const allPrefixes = allLanguageCodes.map(code => code.split("-")[0]);

        if (pathSegments.length > 0) {
            const firstSegment = pathSegments[0];
            if (allPrefixes.includes(firstSegment)) {
                pathSegments[0] = langPrefix;
            } else {
                pathSegments.unshift(langPrefix);
            }
        } else {
            pathSegments.unshift(langPrefix);
        }
    }

    url.pathname = `/${pathSegments.join("/")}`;

    return url.toString();
}
